import React, { useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import { formatCurrency } from "utils/HelperFunctions";
import { resolvePaymentAmountLimits } from "utils/paymentAmountValidation";

interface InputFieldProps {
  type?: string;
  error?: string;
  labelFont?: string;
  isError?: boolean;
  value?: string | number;
  name?: string;
  label?: string;
  placeholder?: string;
  noLabel?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: any;
  disabled?: boolean;
  height?: string;
  fontSize?: string;
  borderBottomRadius?: string;
  borderTopRadius?: string;
  isDollar?: boolean;
  isPercentage?: boolean;
  maxLength?: number;
  onKeyDown?: any;
  className?: string; // Add className prop
  // Anything else (inputMode, aria-label, data-testid...) is forwarded to the
  // underlying <input>. Previously these were silently dropped, which left the
  // amount field unlabelled for screen readers and gave phones the alphabetic
  // keyboard instead of a number pad.
  [key: string]: any;
}

const CustomAmountInput = ({
  inputValue = "",
  setinputValue,
  label,
  noLabel = true,
  disabled = false,
  _style,
  isPercentage = false,
  isDollar = true,
  isError,
  onBlur,
  checkAmt = true,
  balance = "0.00",
  showErr = true,
  setErr,
  isCheckAllowed = true,
  allowNegative = false,
  className = "",
  _className = "",
  isBudgetCalc = false,
  allowOutOfRange = false,
  validateOnChange = false,
  ...rest
}: any) => {
  const { generalSettings } = useAppSelector(
    (state: RootState) => state.payment,
  );
  const [error, setError] = useState<string | null>("");

  const getAmountError = (value: string): string | null => {
    const normalizedValue = value.replace(/[$,]/g, "").trim();
    const amount = normalizedValue === "" ? Number.NaN : Number(normalizedValue);
    const { min, max, isConfigured } = resolvePaymentAmountLimits(
      generalSettings,
      balance,
    );

    if (!isConfigured) {
      return "Payment limits are unavailable. Please refresh and try again.";
    }

    if (!Number.isFinite(amount)) {
      return "Enter a valid payment amount.";
    }

    if (amount > max) {
      return `Amount cannot exceed the configured maximum of ${formatCurrency(
        max,
      )}.`;
    }

    if (amount < min) {
      return `Amount must be at least the configured minimum of ${formatCurrency(
        min,
      )}.`;
    }

    return null;
  };

  const updateAmountError = (value: string): string | null => {
    const message = getAmountError(value);
    setErr(message);
    if (showErr) setError(message);
    return message;
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateAmountError(e.target.value);
  };

  return (
    <div className="relative">
      <NumericFormat
        className={`${className}  ${
          isBudgetCalc ? "!bg-bgSecondary !border-none !text-txtParagraph" : ""
        }`}
        // style={{ boxShadow: `0px 0px 4px 0px ${"#ff0000"}` }}
        customInput={InputField} // Pass the component type
        isAllowed={(values) => {
          if (isCheckAllowed) {
            if (isPercentage) {
              const { floatValue } = values;
              return floatValue ? floatValue <= parseFloat("100.00") : true;
            } else if (allowOutOfRange) {
              // Keep the attempted value visible so the user gets a clear
              // configured-limit error instead of a silently changed amount.
              return true;
            } else {
              const { max, isConfigured } = resolvePaymentAmountLimits(
                generalSettings,
                balance,
              );
              const { floatValue } = values;
              return isConfigured && floatValue ? floatValue <= max : true;
            }
          } else {
            return true;
          }
        }}
        isError={checkAmt ? error : isError}
        onBlur={checkAmt ? handleBlur : onBlur}
        placeholder="0.00"
        noLabel={noLabel}
        label={label}
        decimalScale={2}
        thousandSeparator=","
        fixedDecimalScale
        allowNegative={allowNegative}
        value={inputValue === "0.00" ? "" : inputValue}
        disabled={disabled}
        onValueChange={({ floatValue, formattedValue }) => {
          // setinputValue(formattedValue);
        }}
        onChange={(e) => {
          const rawValue = e?.target?.value || "";
          const normalizedAmount = rawValue
            ? e?.target?.value?.replace(/,/g, "")
            : "0.00";
          const validationError =
            checkAmt && validateOnChange
              ? updateAmountError(rawValue)
              : null;

          if (!validateOnChange) {
            setError(null);
            checkAmt && setErr(null);
          }

          setinputValue(normalizedAmount, validationError);
        }}
        inputMode="decimal"
        allowedDecimalSeparators={["."]} // Correct usage
        {...rest}
      />

      {error && showErr && (
        <div className="text-xs text-red" style={{ whiteSpace: "pre" }}>
          {error}
        </div>
      )}
      {isDollar && (
        <div
          className={`absolute left-[14px] top-[11px] text-sm leading-[18px] text-plainText ${_className}`}
          style={_style}
        >
          $
        </div>
      )}
      {isPercentage && (
        <div
          className={`absolute right-[14px] top-[11px] text-sm leading-[18px] text-plainText ${_className}`}
          style={_style}
        >
          %
        </div>
      )}
    </div>
  );
};

const InputField = ({
  type = "",
  error = "",
  labelFont,
  isError = false,
  value = "",
  name = "name",
  label = "label",
  placeholder = "Placeholder",
  noLabel = false,
  onChange = () => {},
  onBlur = () => {},
  disabled = false,
  height = "",
  fontSize = "",
  borderBottomRadius = "",
  borderTopRadius = "",
  isDollar = false,
  isPercentage = false,
  maxLength = undefined,
  onKeyDown,
  className = "", // Accept className prop
  ...rest
}: InputFieldProps) => {
  const quantityInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const ignoreScroll = (e: WheelEvent) => {
      e.preventDefault();
    };
    quantityInputRef.current?.addEventListener("wheel", ignoreScroll);
    return () => {
      quantityInputRef.current?.removeEventListener("wheel", ignoreScroll);
    };
  }, []);

  return (
    <div className="relative">
      <div className="absolute bottom-[-20px] text-xs text-red">{error}</div>
      {!noLabel && (
        <div
          style={{ fontSize: labelFont ? labelFont : "" }}
          className="text-xs uppercase font-bold text-txtSecondary mb-[3px] leading-[15.62px]"
        >
          {label}
        </div>
      )}
      <div className="relative">
        <input
          style={{
            boxShadow: "none !important",
            WebkitAppearance: "none",
            height: height ? height : "",
            fontSize: fontSize ? fontSize : "",
            borderColor: error ? "#FFCCCC " : "",
            borderBottomLeftRadius:
              borderBottomRadius === "none" ? "0px" : "7px",
            borderBottomRightRadius:
              borderBottomRadius === "none" ? "0px" : "7px",
            borderTopLeftRadius: borderTopRadius === "none" ? "0px" : "7px",
            borderTopRightRadius: borderTopRadius === "none" ? "0px" : "7px",
          }}
          ref={quantityInputRef}
          onBlur={onBlur}
          type={type}
          autoComplete="off"
          className={`dcinput ${disabled ? "bg-bgThird" : "bg-white"} ${
            isDollar && value !== "" && "!px-[23px]"
          } ${isPercentage && value !== "" && "!px-[26px]"} ${
            isError
              ? "border-red"
              : "border-strokePrimary hover:!border-strokeActive focus:border-strokeActive"
          } focus-visible:none focus-visible:ring-none 
            placeholder-txtSecondary h-[40px] w-full rounded-[7px] pl-[10px] py-[10px] text-sm border-[1px] outline-none focus-within:!outline-none focus:!outline-none ${className}`} // Add className to input
          placeholder={placeholder}
          name={name}
          onChange={onChange}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          {...rest}
        />
      </div>
    </div>
  );
};

export default CustomAmountInput;
